import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { EMISSION_SOURCE_OPTIONS } from '../../../shared/constants/emission-sources';
import { conversionCode, formatConversionCodeLabel, sortByConversionCode } from '../../../shared/utils/conversion-utils';
import { Autocomplete, Box, Button, Divider, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, Typography, Stack, InputAdornment, Chip, Tooltip } from '@mui/material';
import { FilterList as FilterListIcon, Search as SearchIcon, Close as CloseIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import LocationService from '../../../shared/services/location/location.service';
import VehicleService from '../../../shared/services/vehicle/vehicle.service';
import ProjectService from '../../../shared/services/project/project.service';
import ContactSupplierService from '../../../shared/services/contact-supplier/contact-supplier.service';
import ManufacturerService from '../../../shared/services/manufacturer/manufacturer.service';
import CategoryService from '../../../shared/services/category/category.service';
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service';
import DataTypeService from '../../../shared/services/data-types/data-types.service';
import GroupService from '../../../shared/services/group/group.service';
import MeasurementUnitService from '../../../shared/services/measurement-unit/measurement-unit.service';
import ConversionSourceService from '../../../shared/services/conversion-source/conversion-source.service';
import SpendBasedConversionService from '../../../shared/services/spend-based-conversion/spend-based-conversion.service';
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service';
import EmissionService from '../../../shared/services/emission/emission.service';
import { IEmissionFactorOption } from '../../../shared/services/emission/emission.model';
import { ICustomField } from '../../../shared/services/custom-field/custom-field.model';
import { ILocation } from '../../../shared/services/location/location.model';
import { IVehicleDetails } from '../../../shared/services/vehicle/vehicle.model';
import { IProject } from '../../../shared/services/project/project.model';
import { IContactSupplierDetails } from '../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../form-field-components/supplier-picker';
import { createVirtualizedListbox } from '../form-field-components/virtualized-listbox';
import { IManufacturer } from '../../../shared/services/manufacturer/manufacturer.model';
import { ICategory } from '../../../shared/services/category/category.model';
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import { IDataTypeRelation } from '../../../shared/services/data-types/data-types.model';
import { IGroupWithCategory } from '../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../shared/constants/ghg-category';
import { IMeasurementUnit } from '../../../shared/services/transaction/transaction.model';
import { IConversionSource } from '../../../shared/services/conversion-source/conversion-source.model';
import { ISpendBasedConversion } from '../../../shared/services/spend-based-conversion/spend-based-conversion.model';
import { DialogWrapper } from '../display-elements-components/dialog-wrapper';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { FLAG_CATEGORY_OPTIONS, formatFlagCategoryDisplay } from '../../../shared/constants/flag-emissions';
import { getCategoryDisplayName, getCategoryGroupLabel, getCategoryGroups, sortCategoriesByDisplayName, sortCategoriesByGroup } from '../../../shared/utils/category-utils';

interface FilterConfig {
  name: string;
  model: string | null;
  inputType: 'input' | 'dropdown' | 'checkbox' | 'date' | 'multiSelect';
}

/** Stringified booleans, because filter values are serialised straight into the query string. */
const FLAG_FILTER_OPTIONS = [
  { id: 'true', name: 'FLAG only' },
  { id: 'false', name: 'Excluding FLAG' },
];


interface CustomFilterProps {
  filters: FilterConfig[];
  onFilterChange?: (filters: Record<string, any>) => void;
  onAppliedFilterStateChange?: (filters: Record<string, any>, filterNames: Record<string, any>) => void;
  filterValues?: Record<string, any>;
  buttonAlignment?: 'left' | 'right';
  showChipsOnly?: boolean;
  showButtonOnly?: boolean;
}

const ITEM_HEIGHT = 48;
const LISTBOX_PADDING = 8;
const LISTBOX_MAX_VISIBLE_ITEMS = 8;

const ScrollableListbox = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { style, ...other } = props;
    return (
      <ul
        ref={ref}
        {...other}
        style={{
          ...style,
          maxHeight: ITEM_HEIGHT * LISTBOX_MAX_VISIBLE_ITEMS + LISTBOX_PADDING * 2,
          overflow: 'auto',
          margin: 0,
          padding: 0,
          listStyle: 'none',
        }}
      />
    );
  }
);

// The multi-select's option rows: a default-size checkbox (42px) plus the option padding. Only the
// supplier list is long enough to need virtualising.
const SupplierMultiSelectListbox = createVirtualizedListbox(54);

export const CustomFilter: React.FC<CustomFilterProps> = ({
  filters,
  onFilterChange,
  onAppliedFilterStateChange,
  filterValues: externalFilterValues,
  buttonAlignment = 'left',
  showChipsOnly = false,
  showButtonOnly = false
}) => {
  const [open, setOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [filterNames, setFilterNames] = useState<Record<string, any>>({});
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [vehicles, setVehicles] = useState<IVehicleDetails[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>([]);
  const [manufacturers, setManufacturers] = useState<IManufacturer[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([]);
  const [dataTypes, setDataTypes] = useState<IDataTypeRelation[]>([]);
  const [groups, setGroups] = useState<IGroupWithCategory[]>([]);
  const [measurementUnits, setMeasurementUnits] = useState<IMeasurementUnit[]>([]);
  const [conversionSources, setConversionSources] = useState<IConversionSource[]>([]);
  const [spendBasedConversions, setSpendBasedConversions] = useState<ISpendBasedConversion[]>([]);
  const [selectedConversionSourceId, setSelectedConversionSourceId] = useState<string>('');
  const [emissionFactorOptions, setEmissionFactorOptions] = useState<IEmissionFactorOption[]>([]);
  const [selectedFactorYear, setSelectedFactorYear] = useState<string>('');
  const [customFields, setCustomFields] = useState<ICustomField[]>([]);
  const [customFieldPairs, setCustomFieldPairs] = useState<{fieldId: string; fieldName: string; value: string; matchMode: 'exact' | 'partial'}[]>([]);
  const [pendingCustomFieldId, setPendingCustomFieldId] = useState<string>('');
  const [pendingCustomFieldValue, setPendingCustomFieldValue] = useState<string>('');
  const [pendingMatchMode, setPendingMatchMode] = useState<'exact' | 'partial'>('exact');
  const [matchType, setMatchType] = useState<'AND' | 'OR'>('AND');
  const [emissionTypes] = useState([
    { id: 'ACTIVITY', name: 'Activity-Based Emissions' },
    { id: 'DIRECT', name: 'Pre-Calculated Emissions' },
    { id: 'SPEND', name: 'Spend-Based Emissions' },
    { id: 'PRODUCT', name: 'Product-Based Emissions' },
    { id: 'MEASUREMENT', name: 'Measurement-Based Emissions' }
  ]);
  const [loading, setLoading] = useState(false);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);
  const activeReportPeriodId = reportPeriodContext?.lastSelectedReportId;
  const activeReportPeriod = reportPeriodContext?.allReportPeriods?.find(period => period.id === activeReportPeriodId);

  const sortedSuppliers = useMemo(
    () => contactSuppliers.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [contactSuppliers]
  );

  useEffect(() => {
    if (!externalFilterValues) return;

    setFilterValues(externalFilterValues);
    setSelectedFilters(
      filters
        .filter((filter) => {
          const value = externalFilterValues[filter.model || filter.name];
          if (Array.isArray(value)) return value.length > 0;
          return value !== undefined && value !== null && value !== '';
        })
        .map((filter) => filter.name)
    );
  }, [externalFilterValues, filters]);

  const getFilterLabel = (filter: FilterConfig): string => {
    switch (filter.model) {
      case 'subsidiary':
        return screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' });
      case 'project':
        return screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' });
      case 'contactSupplier':
        return screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' });
      case 'location':
        return screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' });
      case 'vehicle':
        return screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' });
      case 'dataType':
        return 'Data Type';
      case 'categoryGroup':
        return 'Category Group';
      case 'group':
        return 'Group';
      case 'measurementUnit':
        return 'Measurement Unit';
      case 'scope':
        return 'Scope';
      case 'emissionType':
        return 'Emission Type';
      case 'source':
        return 'Source';
      case 'isFlag':
        return 'FLAG';
      case 'flagCategory':
        return 'FLAG category';
      case 'emissionFactor':
        return 'Emission Factor';
      default:
        return filter.name;
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    // Reset conversion source selection when closing
    setSelectedConversionSourceId('');
    setSpendBasedConversions([]);
    setOpen(false);
  };

  const handleFilterSelect = (filterName: string) => {
    setSelectedFilters(prev => {
      if (prev.includes(filterName)) {
        return prev.filter(name => name !== filterName);
      }
      return [...prev, filterName];
    });
  };

  const handleCheckboxChange = (filterName: string, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    handleFilterSelect(filterName);
  };

  const getDisplayName = useCallback((id: string, model: string | null): string => {
    if (!model) return id; // For input fields without models, return the value as-is

    switch (model) {
      case 'activity':
      case 'activities':
        const conversion = spendBasedConversions.find(c => c.id === id);
        return conversion ? formatConversionCodeLabel(conversion) : id;
      case 'vehicle':
        const vehicle = vehicles.find(v => v.id === id);
        return vehicle ? vehicle.customUniqueId : id;
      case 'location':
        const location = locations.find(l => l.id === id);
        return location ? location.friendlyName : id;
      case 'country':
        const country = countries.find(c => c.id === id);
        return country ? country.name : id;
      case 'project':
        const project = projects.find(p => p.id === id);
        return project ? project.summary : id;
      case 'contactSupplier':
        const supplier = contactSuppliers.find(s => s.id === id);
        return supplier ? supplier.name : id;
      case 'manufacturer':
        const manufacturer = manufacturers.find(m => m.id === id);
        return manufacturer ? manufacturer.name : id;
      case 'category':
        const category = categories.find(c => c.id === id);
        return category ? getCategoryDisplayName(category) : id;
      case 'categoryGroup':
        return getCategoryGroups(categories).find(group => group.id === id)?.name ?? id;
      case 'subsidiary':
        const subsidiary = subsidiaries.find(s => s.id === id);
        return subsidiary ? subsidiary.name : id;
      case 'dataType':
        const dataType = dataTypes.find(d => d.id === id);
        return dataType ? dataType.dataType : id;
      case 'group':
        const group = groups.find(g => g.id === id);
        // Same text as the option in the Group dropdown, so the chip and the list agree.
        return group ? formatGroupLabel(group, { includeScope: false }) : id;
      case 'measurementUnit':
        const unit = measurementUnits.find(m => m.id === id);
        return unit ? unit.name : id;
      case 'emissionType':
        const emissionType = emissionTypes.find(e => e.id === id);
        return emissionType ? emissionType.name : id;
      case 'source':
        const sourceOption = EMISSION_SOURCE_OPTIONS.find(o => o.id === id);
        return sourceOption ? sourceOption.name : id;
      case 'isFlag':
        const flagOption = FLAG_FILTER_OPTIONS.find(o => o.id === id);
        return flagOption ? flagOption.name : id;
      case 'flagCategory':
        return formatFlagCategoryDisplay(id);
      case 'emissionFactor':
        const factorOption = emissionFactorOptions.find(o => o.id === id);
        return factorOption ? factorOption.label : id;
      default:
        return id;
    }
  }, [vehicles, locations, countries, projects, contactSuppliers, manufacturers, categories, subsidiaries, dataTypes, groups, measurementUnits, emissionTypes, spendBasedConversions, emissionFactorOptions]);

  const hasDataForFilter = (filter: FilterConfig): boolean => {
    if (!filter.model) return true; // Input fields without models are always available

    let hasData = false;
    switch (filter.model) {
      case 'vehicle':
        hasData = vehicles && vehicles.length > 0;
        break;
      case 'location':
        hasData = locations && locations.length > 0;
        break;
      case 'country':
        hasData = countries && countries.length > 0;
        break;
      case 'project':
        hasData = projects && projects.length > 0;
        break;
      case 'contactSupplier':
        hasData = contactSuppliers && contactSuppliers.length > 0;
        break;
      case 'manufacturer':
        hasData = manufacturers && manufacturers.length > 0;
        break;
      case 'category':
        hasData = categories && categories.length > 0;
        break;
      case 'categoryGroup':
        hasData = getCategoryGroups(categories).length > 0;
        break;
      case 'subsidiary':
        hasData = subsidiaries && subsidiaries.length > 0;
        break;
      case 'dataType':
        hasData = dataTypes && dataTypes.length > 0;
        break;
      case 'group':
        hasData = groups && groups.length > 0;
        break;
      case 'measurementUnit':
        hasData = measurementUnits && measurementUnits.length > 0;
        break;
      case 'scope':
        hasData = true;
        break;
      case 'emissionType':
        hasData = true;
        break;
      case 'source':
        hasData = true;
        break;
      case 'customField':
        hasData = customFields.length > 0;
        break;
      default:
        hasData = true;
    };

    return hasData;
  };

  // Fetch all data when component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);

      // Get unique models from filter configuration
      const modelsToFetch = new Set(
        filters
          .map(filter => filter.model)
          .filter(model => model !== null)
      );

      const promises: Promise<any>[] = [];
      const promiseIndexMap: { [key: string]: number } = {};

      // Only fetch data for models that are actually used
      if (modelsToFetch.has('location')) {
        promiseIndexMap.location = promises.length;
        promises.push(LocationService.getInstance().getAllLocations());
      }

      if (modelsToFetch.has('vehicle')) {
        promiseIndexMap.vehicle = promises.length;
        promises.push(VehicleService.getInstance().getAllVehicles());
      }

      if (modelsToFetch.has('country')) {
        promiseIndexMap.country = promises.length;
        promises.push(LocationService.getInstance().getAllCountries());
      }

      if (modelsToFetch.has('project')) {
        promiseIndexMap.project = promises.length;
        promises.push(ProjectService.getInstance().getAll());
      }

      if (modelsToFetch.has('contactSupplier')) {
        promiseIndexMap.contactSupplier = promises.length;
        promises.push(ContactSupplierService.getInstance().getAllSuppliersLite());
      }

      if (modelsToFetch.has('manufacturer')) {
        promiseIndexMap.manufacturer = promises.length;
        promises.push(new ManufacturerService().getAll());
      }

      // Category groups are read off the category list, so either filter needs it.
      if (modelsToFetch.has('category') || modelsToFetch.has('categoryGroup')) {
        promiseIndexMap.category = promises.length;
        promises.push(new CategoryService().getAllCategories());
      }

      if (modelsToFetch.has('subsidiary')) {
        promiseIndexMap.subsidiary = promises.length;
        promises.push(SubsidiaryService.getInstance().getAll());
      }

      if (modelsToFetch.has('dataType')) {
        promiseIndexMap.dataType = promises.length;
        promises.push(new DataTypeService().getAll());
      }

      if (modelsToFetch.has('group')) {
        promiseIndexMap.group = promises.length;
        promises.push(new GroupService().getGroupsWithCategories(false));
      }

      if (modelsToFetch.has('measurementUnit')) {
        promiseIndexMap.measurementUnit = promises.length;
        promises.push(new MeasurementUnitService().getAll());
      }

      if (modelsToFetch.has('customField')) {
        promiseIndexMap.customField = promises.length;
        promises.push(new CustomFieldService().getAllActive('&entryType=transaction'));
      }

      try {
        const results = await Promise.allSettled(promises);

        // Set data for each service, handling both success and failure cases
        if (promiseIndexMap.location !== undefined && results[promiseIndexMap.location].status === 'fulfilled') {
          setLocations((results[promiseIndexMap.location] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.vehicle !== undefined && results[promiseIndexMap.vehicle].status === 'fulfilled') {
          setVehicles((results[promiseIndexMap.vehicle] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.country !== undefined && results[promiseIndexMap.country].status === 'fulfilled') {
          setCountries((results[promiseIndexMap.country] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.project !== undefined && results[promiseIndexMap.project].status === 'fulfilled') {
          setProjects((results[promiseIndexMap.project] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.contactSupplier !== undefined && results[promiseIndexMap.contactSupplier].status === 'fulfilled') {
          setContactSuppliers((results[promiseIndexMap.contactSupplier] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.manufacturer !== undefined && results[promiseIndexMap.manufacturer].status === 'fulfilled') {
          setManufacturers((results[promiseIndexMap.manufacturer] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.category !== undefined && results[promiseIndexMap.category].status === 'fulfilled') {
          setCategories((results[promiseIndexMap.category] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.subsidiary !== undefined && results[promiseIndexMap.subsidiary].status === 'fulfilled') {
          setSubsidiaries((results[promiseIndexMap.subsidiary] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.dataType !== undefined && results[promiseIndexMap.dataType].status === 'fulfilled') {
          setDataTypes((results[promiseIndexMap.dataType] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.group !== undefined && results[promiseIndexMap.group].status === 'fulfilled') {
          setGroups((results[promiseIndexMap.group] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.measurementUnit !== undefined && results[promiseIndexMap.measurementUnit].status === 'fulfilled') {
          setMeasurementUnits((results[promiseIndexMap.measurementUnit] as PromiseFulfilledResult<any>).value || []);
        }

        if (promiseIndexMap.customField !== undefined && results[promiseIndexMap.customField].status === 'fulfilled') {
          setCustomFields((results[promiseIndexMap.customField] as PromiseFulfilledResult<any>).value || []);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [filters]); // Run when filters change

  // Fetch conversion sources when Activity filter is selected
  useEffect(() => {
    const activityFilter = filters.find(f => f.name === 'Activity');
    if (activityFilter && selectedFilters.includes('Activity')) {
      const conversionSourceService = new ConversionSourceService();
      conversionSourceService.getAll(true, 'spendBasedConversion')
        .then(response => {
          setConversionSources(response || []);
        })
        .catch(err => {
          console.error('Error fetching conversion sources:', err);
          setConversionSources([]);
        });
    }
  }, [filters, selectedFilters]);

  // Fetch conversions when conversion source is selected
  useEffect(() => {
    if (selectedConversionSourceId) {
      const spendBasedConversionService = new SpendBasedConversionService();
      const currentYear = new Date().getFullYear().toString();
      spendBasedConversionService.getSpendBasedConversionsForConversionSource(
        selectedConversionSourceId,
        currentYear,
        true,  // closestYear
        true   // active
      )
        .then(response => {
          setSpendBasedConversions(response || []);
        })
        .catch(err => {
          console.error('Error fetching conversions:', err);
          setSpendBasedConversions([]);
        });
    } else {
      setSpendBasedConversions([]);
    }
  }, [selectedConversionSourceId]);

  // Fetch the factors used in the active report period when the Emission Factor filter is selected,
  // or when it already has applied values (so chips can show labels instead of raw ids after reload)
  useEffect(() => {
    const factorFilter = filters.find(f => f.model === 'emissionFactor');
    const hasAppliedFactorValues = Array.isArray(filterValues['emissionFactor']) && filterValues['emissionFactor'].length > 0;
    if (!factorFilter || !activeReportPeriodId) return;
    if (!selectedFilters.includes('Emission Factor') && !hasAppliedFactorValues) return;
    new EmissionService().getFactorOptions(activeReportPeriodId)
      .then(response => {
        setEmissionFactorOptions(response?.rows || []);
      })
      .catch(err => {
        console.error('Error fetching emission factor options:', err);
        setEmissionFactorOptions([]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedFilters, activeReportPeriodId, Array.isArray(filterValues['emissionFactor']) && filterValues['emissionFactor'].length > 0]);

  // Default the factor year to the active report period's factor year once options are loaded
  useEffect(() => {
    if (emissionFactorOptions.length === 0 || selectedFactorYear !== '') return;
    const years = Array.from(new Set(emissionFactorOptions.map(o => o.calendarYear).filter(Boolean))) as string[];
    if (years.length === 0) return;
    years.sort().reverse();
    const periodYear = activeReportPeriod?.conversionFiguresYear ? String(activeReportPeriod.conversionFiguresYear) : null;
    setSelectedFactorYear(periodYear && years.includes(periodYear) ? periodYear : years[0]);
  }, [emissionFactorOptions, selectedFactorYear, activeReportPeriod]);

  // Restore conversion source when activities are already selected
  useEffect(() => {
    const activitiesValue = filterValues['activity'];
    if (Array.isArray(activitiesValue) && activitiesValue.length > 0 && !selectedConversionSourceId) {
      // Try to find the conversion source from the first activity ID
      // We'll need to fetch all conversions to find the source, or store it separately
      // For now, we'll leave it - user will need to reselect the source
    }
  }, [filterValues, selectedConversionSourceId]);

  // Sync customFieldPairs from filterValues when customFields data is loaded
  useEffect(() => {
    const raw = filterValues['customField'];
    if (!Array.isArray(raw) || raw.length === 0) {
      setCustomFieldPairs([]);
      return;
    }
    if (customFields.length === 0) return;
    const pairs = raw
      .map((s: string) => {
        const firstSep = s.indexOf('::');
        if (firstSep === -1) return null;
        const fieldId = s.slice(0, firstSep);
        const rest = s.slice(firstSep + 2);
        const secondSep = rest.indexOf('::');
        const matchMode = (secondSep !== -1 ? rest.slice(0, secondSep) : 'exact') as 'exact' | 'partial';
        const value = secondSep !== -1 ? rest.slice(secondSep + 2) : rest;
        const field = customFields.find(f => f.id === fieldId);
        return field ? { fieldId, fieldName: field.fieldName, value, matchMode } : null;
      })
      .filter(Boolean) as {fieldId: string; fieldName: string; value: string; matchMode: 'exact' | 'partial'}[];
    setCustomFieldPairs(pairs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, filterValues['customField']]);

  // Restore matchType from external filter values
  useEffect(() => {
    const mt = filterValues['customFieldMatchType'];
    setMatchType(mt === 'OR' ? 'OR' : 'AND');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues['customFieldMatchType']]);

  // Update filterNames when data arrays change (for existing filter values)
  useEffect(() => {
    const updateExistingFilterNames = () => {
      const updatedNames: Record<string, any> = {};

      Object.entries(filterValues).forEach(([key, value]) => {
        const filter = filters.find(f => (f.model || f.name) === key);
        if (filter) {
          // For activity filters, if we don't currently have spend-based conversions loaded,
          // preserve any existing display names instead of falling back to IDs.
          if ((filter.model === 'activity' || filter.model === 'activities') && spendBasedConversions.length === 0 && filterNames[key]) {
            updatedNames[key] = filterNames[key];
            return;
          }

          // Same for emission factor filters: option labels load lazily, so keep existing
          // display names until the fetch resolves rather than downgrading chips to raw ids.
          if (filter.model === 'emissionFactor' && emissionFactorOptions.length === 0 && filterNames[key]) {
            updatedNames[key] = filterNames[key];
            return;
          }

          if (Array.isArray(value)) {
            updatedNames[key] = value.map(id => getDisplayName(id, filter.model));
          } else if (value && typeof value === 'string' && filter.model) {
            updatedNames[key] = getDisplayName(value, filter.model);
          } else {
            updatedNames[key] = value;
          }
        }
      });

      setFilterNames(updatedNames);
    };

    // Only update if we have data loaded and existing filter values
    if (Object.keys(filterValues).length > 0 &&
      (vehicles.length > 0 || locations.length > 0 || countries.length > 0 ||
        projects.length > 0 || contactSuppliers.length > 0 || manufacturers.length > 0 || categories.length > 0 ||
        spendBasedConversions.length > 0 || emissionFactorOptions.length > 0)) {
      updateExistingFilterNames();
    }
  }, [vehicles, locations, countries, projects, contactSuppliers, manufacturers, categories, spendBasedConversions, emissionFactorOptions, filterValues, filters, getDisplayName, filterNames]);

  const handleValueChange = (currentFilter: FilterConfig, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [currentFilter.model || currentFilter.name]: value
    }));

    // Update filterNames with display names
    const updateFilterNames = (updatedValue: any) => {
      if (Array.isArray(updatedValue)) {
        // Handle multi-select arrays
        return updatedValue.map(id => getDisplayName(id, currentFilter.model));
      } else if (updatedValue && typeof updatedValue === 'string' && currentFilter.model) {
        // Handle single select
        return getDisplayName(updatedValue, currentFilter.model);
      } else {
        // Handle input fields or other types
        return updatedValue;
      }
    };

    setFilterNames(prev => ({
      ...prev,
      [currentFilter.model || currentFilter.name]: updateFilterNames(value)
    }));
  };

  const handleRemoveFilter = (filterKey: string, valueToRemove: string) => {
    const currentValue = filterValues[filterKey];
    let updatedFilterValues: Record<string, any> = { ...filterValues };

    if (Array.isArray(currentValue)) {
      // Handle multi-select arrays
      const newValue = currentValue.filter(id => id !== valueToRemove);
      if (newValue.length === 0) {
        // Remove the entire filter if no values left
        delete updatedFilterValues[filterKey];
        setFilterValues(updatedFilterValues);
        setFilterNames(prev => {
          const newNames = { ...prev };
          delete newNames[filterKey];
          return newNames;
        });
      } else {
        // Update with remaining values
        updatedFilterValues[filterKey] = newValue;
        setFilterValues(updatedFilterValues);

        // Update filterNames with remaining display names
        const filter = filters.find(f => (f.model || f.name) === filterKey);
        const newDisplayNames = newValue.map(id => getDisplayName(id, filter?.model || null));
        setFilterNames(prev => ({
          ...prev,
          [filterKey]: newDisplayNames
        }));
      }
    } else {
      // Handle single select - remove the entire filter
      delete updatedFilterValues[filterKey];
      setFilterValues(updatedFilterValues);
      setFilterNames(prev => {
        const newNames = { ...prev };
        delete newNames[filterKey];
        return newNames;
      });
    }

    // Re-send the EP with updated filters
    if (onFilterChange) {
      onFilterChange(updatedFilterValues);
    }
    if (onAppliedFilterStateChange) {
      const updatedNames = Array.isArray(currentValue)
        ? (() => {
            if ((currentValue.filter(id => id !== valueToRemove)).length === 0) {
              const newNames = { ...filterNames };
              delete newNames[filterKey];
              return newNames;
            }
            return {
              ...filterNames,
              [filterKey]: (currentValue.filter(id => id !== valueToRemove)).map(id => {
                const filter = filters.find(f => (f.model || f.name) === filterKey);
                return getDisplayName(id, filter?.model || null);
              })
            };
          })()
        : (() => {
            const newNames = { ...filterNames };
            delete newNames[filterKey];
            return newNames;
          })();
      onAppliedFilterStateChange(updatedFilterValues, updatedNames);
    }
  };

  const handleClearAll = () => {
    setFilterValues({});
    setFilterNames({});
    setSelectedFilters([]);
    setSelectedConversionSourceId('');
    setSpendBasedConversions([]);
    setSelectedFactorYear('');
    setCustomFieldPairs([]);
    setPendingCustomFieldId('');
    setPendingCustomFieldValue('');
    setPendingMatchMode('exact');
    setMatchType('AND');
    if (onFilterChange) {
      onFilterChange({});
    }
    if (onAppliedFilterStateChange) {
      onAppliedFilterStateChange({}, {});
    }
  };

  const renderFilterChips = () => {
    const chips: JSX.Element[] = [];

    Object.entries(filterNames).forEach(([filterKey, displayNames]) => {
      const filter = filters.find(f => (f.model || f.name) === filterKey);
      const filterLabel = filter?.name || filterKey;

      if (filterKey === 'customField' && Array.isArray(displayNames)) {
        displayNames.forEach((displayName, index) => {
          const correspondingId = Array.isArray(filterValues[filterKey])
            ? filterValues[filterKey][index]
            : null;
          if (correspondingId) {
            chips.push(
              <Chip
                key={`customField-${correspondingId}`}
                label={displayName}
                onDelete={() => handleRemoveFilter(filterKey, correspondingId)}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                size="small"
              />
            );
          }
        });
        return;
      }

      if (Array.isArray(displayNames)) {
        // Multi-select values
        displayNames.forEach((displayName, index) => {
          const correspondingId = Array.isArray(filterValues[filterKey])
            ? filterValues[filterKey][index]
            : null;

          if (correspondingId) {
            chips.push(
              <Chip
                key={`${filterKey}-${correspondingId}`}
                label={`${filterLabel}='${displayName}'`}
                onDelete={() => handleRemoveFilter(filterKey, correspondingId)}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                size="small"
              />
            );
          }
        });
      } else if (displayNames) {
        // Single select values
        const correspondingId = filterValues[filterKey];
        if (correspondingId) {
          chips.push(
            <Chip
              key={`${filterKey}-${correspondingId}`}
              label={`${filterLabel}='${displayNames}'`}
              onDelete={() => handleRemoveFilter(filterKey, correspondingId)}
              deleteIcon={<CloseIcon />}
              variant="outlined"
              size="small"
            />
          );
        }
      }
    });

    // Add "Clear all" chip if there are multiple filters
    const totalFilterCount = Object.values(filterNames).reduce((total, displayNames) => {
      if (Array.isArray(displayNames)) {
        return total + displayNames.length;
      } else if (displayNames) {
        return total + 1;
      }
      return total;
    }, 0);

    if (totalFilterCount > 1) {
      chips.push(
        <Chip
          key="clear-all"
          label="Clear all"
          onClick={handleClearAll}
          variant="filled"
          color="secondary"
          size="small"
          sx={{ cursor: 'pointer' }}
        />
      );
    }

    return chips;
  };

  const addCustomFieldPair = () => {
    const field = customFields.find(f => f.id === pendingCustomFieldId);
    if (!field || !pendingCustomFieldValue.trim()) return;
    setCustomFieldPairs(prev => [...prev, { fieldId: field.id, fieldName: field.fieldName, value: pendingCustomFieldValue.trim(), matchMode: pendingMatchMode }]);
    setPendingCustomFieldId('');
    setPendingCustomFieldValue('');
    setPendingMatchMode('exact');
  };

  const handleSave = () => {
    let savedFilterValues = { ...filterValues };
    let savedFilterNames = { ...filterNames };

    if (customFieldPairs.length > 0) {
      savedFilterValues = {
        ...savedFilterValues,
        customField: customFieldPairs.map(p => `${p.fieldId}::${p.matchMode}::${p.value}`),
        customFieldMatchType: matchType
      };
      savedFilterNames = {
        ...savedFilterNames,
        customField: customFieldPairs.map(p => p.matchMode === 'partial' ? `${p.fieldName}~'${p.value}'` : `${p.fieldName}='${p.value}'`)
      };
    } else {
      delete savedFilterValues['customField'];
      delete savedFilterValues['customFieldMatchType'];
      delete savedFilterNames['customField'];
    }

    if (onFilterChange) {
      onFilterChange(savedFilterValues);
    }
    if (onAppliedFilterStateChange) {
      onAppliedFilterStateChange(savedFilterValues, savedFilterNames);
    }
    handleClose();
  };

  const renderFilterInput = (currentFilter: FilterConfig) => {
    const currentValue = filterValues[currentFilter.model || currentFilter.name] || '';
    const displayLabel = getFilterLabel(currentFilter);

    // Custom Field filter: match type selector + field/value/matchMode row + add button
    if (currentFilter.model === 'customField') {
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Match type:</Typography>
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Match type</Typography>
                  <Typography variant="body2"><strong>AND</strong>: only show rows that match every condition you add.</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>OR</strong>: show rows that match any one of the conditions.</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Match mode (per condition)</Typography>
                  <Typography variant="body2"><strong>Exact match</strong>: the field value must equal your input exactly (case-insensitive).</Typography>
                  <Typography variant="body2"><strong>Partial match</strong>: the field value just needs to contain your input, e.g. typing "red" would match "Predefined" or "Redirect".</Typography>
                </Box>
              }
              placement="right"
              arrow
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'default' }} />
            </Tooltip>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={matchType}
                onChange={(e) => setMatchType(e.target.value as 'AND' | 'OR')}
              >
                <MenuItem value="AND">AND</MenuItem>
                <MenuItem value="OR">OR</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
            <Autocomplete
              options={customFields.slice().sort((a, b) => a.fieldName.localeCompare(b.fieldName))}
              getOptionLabel={(o) => (o as ICustomField).fieldName}
              value={customFields.find(f => f.id === pendingCustomFieldId) || null}
              onChange={(_, v) => setPendingCustomFieldId((v as ICustomField)?.id || '')}
              renderInput={(params) => <TextField {...params} label="Custom Field" />}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Value"
              value={pendingCustomFieldValue}
              onChange={(e) => setPendingCustomFieldValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pendingCustomFieldId && pendingCustomFieldValue.trim()) {
                  addCustomFieldPair();
                }
              }}
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Match mode</InputLabel>
              <Select
                label="Match mode"
                value={pendingMatchMode}
                onChange={(e) => setPendingMatchMode(e.target.value as 'exact' | 'partial')}
              >
                <MenuItem value="exact">Exact match</MenuItem>
                <MenuItem value="partial">Partial match</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              disabled={!pendingCustomFieldId || !pendingCustomFieldValue.trim()}
              onClick={addCustomFieldPair}
            >
              Add
            </Button>
          </Box>
          {customFieldPairs.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {customFieldPairs.map((pair, i) => (
                <Chip
                  key={`${pair.fieldId}-${i}`}
                  label={pair.matchMode === 'partial' ? `${pair.fieldName}~'${pair.value}'` : `${pair.fieldName}='${pair.value}'`}
                  onDelete={() => setCustomFieldPairs(prev => prev.filter((_, idx) => idx !== i))}
                  deleteIcon={<CloseIcon />}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}
        </Box>
      );
    }

    // Emission Factor filter: a factor year select (defaulted to the active report period's
    // factor year) cascading into a multi-select of the factors used in that year
    if (currentFilter.model === 'emissionFactor') {
      const factorValue: string[] = Array.isArray(filterValues['emissionFactor']) ? filterValues['emissionFactor'] : [];
      const factorYears = (Array.from(new Set(emissionFactorOptions.map(o => o.calendarYear).filter(Boolean))) as string[])
        .sort()
        .reverse();
      const yearOptions = emissionFactorOptions
        .filter(o => o.calendarYear === selectedFactorYear)
        .sort((a, b) => (a.sourceName || '').localeCompare(b.sourceName || '') || a.label.localeCompare(b.label));
      const selectedFactorOptions = emissionFactorOptions.filter(o => factorValue.includes(o.id));

      if (emissionFactorOptions.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            No emission factors have been used in this reporting period yet.
          </Typography>
        );
      }

      return (
        <Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Factor Year</InputLabel>
            <Select
              label="Factor Year"
              value={selectedFactorYear}
              onChange={(e) => {
                const year = e.target.value as string;
                setSelectedFactorYear(year);
                // Drop selections that belong to other years so the applied filter matches the visible list
                const idsForYear = factorValue.filter(id =>
                  emissionFactorOptions.some(o => o.id === id && o.calendarYear === year)
                );
                if (idsForYear.length !== factorValue.length) {
                  handleValueChange(currentFilter, idsForYear);
                }
              }}
            >
              {factorYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            disableCloseOnSelect
            ListboxComponent={ScrollableListbox}
            options={yearOptions}
            groupBy={(option) => (option as IEmissionFactorOption).sourceName || 'Other'}
            getOptionLabel={(option) => (option as IEmissionFactorOption).label}
            value={selectedFactorOptions}
            onChange={(_, newValue) => {
              const ids = (newValue as IEmissionFactorOption[]).map(o => o.id);
              handleValueChange(currentFilter, ids);
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
              return (
                <Box
                  component="li"
                  key={key}
                  {...rest}
                >
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {(option as IEmissionFactorOption).label}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Emission Factor"
              />
            )}
          />
        </Box>
      );
    }

    // Special handling for Activity filter
    if (currentFilter.name === 'Activity') {
      const activitiesValue = filterValues['activity'] || [];
      const selectedConversions = Array.isArray(activitiesValue)
        ? spendBasedConversions.filter(conv => activitiesValue.includes(conv.id))
        : [];

      return (
        <Box>
          <Autocomplete
            options={conversionSources}
            getOptionLabel={(option) => option.source || ''}
            value={conversionSources.find(cs => cs.id === selectedConversionSourceId) || null}
            onChange={(_, newValue) => {
              const sourceId = (newValue as IConversionSource)?.id || '';
              setSelectedConversionSourceId(sourceId);
              if (!sourceId) {
                // Clear conversions when source is cleared
                setSpendBasedConversions([]);
                handleValueChange({ name: 'Activity', model: 'activity', inputType: 'input' }, []);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Spend Conversion Factor Source"
              />
            )}
            sx={{ mb: 2 }}
          />
          {selectedConversionSourceId && spendBasedConversions.length > 0 && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={sortByConversionCode(spendBasedConversions)}
              getOptionLabel={(option) => formatConversionCodeLabel(option)}
              value={selectedConversions}
              onChange={(_, newValue) => {
                const ids = (newValue as ISpendBasedConversion[]).map(conv => conv.id);
                const displayNames = (newValue as ISpendBasedConversion[]).map(conv =>
                  formatConversionCodeLabel(conv)
                );
                // Store under 'activity' to match the model used in the config
                setFilterValues(prev => ({
                  ...prev,
                  activity: ids
                }));
                setFilterNames(prev => ({
                  ...prev,
                  activity: displayNames
                }));
              }}
              filterOptions={(opts, { inputValue }) => {
                const search = inputValue.trim().toLowerCase();
                if (!search) return opts;
                return opts.filter(option =>
                  (option.activity || '').toLowerCase().includes(search) ||
                  conversionCode(option).toLowerCase().includes(search)
                );
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...rest}
                  >
                    <Checkbox
                      sx={{ mr: 1 }}
                      checked={selected}
                    />
                    {formatConversionCodeLabel(option)}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Spend Activity"
                />
              )}
            />
          )}
        </Box>
      );
    }

    switch (currentFilter.inputType) {
      case 'input':
        return (
          <TextField
            fullWidth
            value={currentValue}
            onChange={(e) => handleValueChange(currentFilter, e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        );

      case 'dropdown':
        switch (currentFilter.model) {
          case 'country':
            return countries && countries.length > 0 ? (
              <Autocomplete
                id="countryId"
                options={countries.slice().sort((a, b) =>
                  a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)
                )}
                getOptionLabel={(option) => option.name}
                value={countries.find(c => c.id === currentValue) || null}
                onChange={(_, newValue) => {
                  handleValueChange(currentFilter, (newValue as any)?.id || '');
                }}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                  return (
                    <Box
                      component="li"
                      sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                      key={key}
                      {...restProps}
                    >
                      <img
                        loading="lazy"
                        width="20"
                        src={option.imageUrl}
                        alt="country flag"
                      />
                      {option.name}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={displayLabel}
                  />
                )}
              />
            ) : null;

          case 'location':
            return locations && locations.length > 0 ? (
              <Autocomplete
                id="locationId"
                options={locations.slice().sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))}
                getOptionLabel={(option) => option.friendlyName}
                value={currentValue ? locations.find(l => l.id === currentValue) || null : null}
                onChange={(_, newValue) => {
                  handleValueChange(currentFilter, (newValue as any)?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={displayLabel}
                  />
                )}
              />
            ) : null;

          case 'vehicle':
            return vehicles && vehicles.length > 0 ? (
              <Autocomplete
                id="vehicleId"
                options={vehicles.slice().sort((a, b) => (a.customUniqueId || '').localeCompare(b.customUniqueId || ''))}
                getOptionLabel={(option) => option.customUniqueId || ''}
                value={currentValue ? vehicles.find(v => v.id === currentValue) || null : null}
                onChange={(_, newValue) => {
                  handleValueChange(currentFilter, (newValue as any)?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={displayLabel}
                  />
                )}
              />
            ) : null;

          case 'project':
            return projects && projects.length > 0 ? (
              <Autocomplete
                id="projectId"
                options={projects.slice().sort((a, b) => (a.summary || '').localeCompare(b.summary || ''))}
                getOptionLabel={(option) => option.summary || ''}
                value={currentValue ? projects.find(p => p.id === currentValue) || null : null}
                onChange={(_, newValue) => {
                  handleValueChange(currentFilter, (newValue as any)?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={displayLabel}
                  />
                )}
              />
            ) : null;

          case 'contactSupplier':
            return contactSuppliers && contactSuppliers.length > 0 ? (
              <SupplierPicker
                id="contactSupplierId"
                suppliers={contactSuppliers}
                label={displayLabel}
                value={currentValue || null}
                onChange={(id) => {
                  handleValueChange(currentFilter, id || '');
                }}
              />
            ) : null;

          case 'manufacturer':
            return manufacturers && manufacturers.length > 0 ? (
              <Autocomplete
                id="manufacturerId"
                options={manufacturers.slice().sort((a, b) => a.name.localeCompare(b.name))}
                getOptionLabel={(option) => option.name}
                value={currentValue ? manufacturers.find(m => m.id === currentValue) || null : null}
                onChange={(_, newValue) => {
                  handleValueChange(currentFilter, (newValue as any)?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={displayLabel}
                  />
                )}
              />
            ) : null;

          case 'isFlag':
            return (
              <FormControl fullWidth>
                <InputLabel>{displayLabel}</InputLabel>
                <Select
                  value={currentValue}
                  onChange={(e) => handleValueChange(currentFilter, e.target.value)}
                  label={displayLabel}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {FLAG_FILTER_OPTIONS.map(option => (
                    <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            );

          default:
            // Fallback simple select
            return (
              <FormControl fullWidth>
                <InputLabel>{displayLabel}</InputLabel>
                <Select
                  value={currentValue}
                  onChange={(e) => handleValueChange(currentFilter, e.target.value)}
                  label={displayLabel}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="option1">Option 1</MenuItem>
                  <MenuItem value="option2">Option 2</MenuItem>
                  <MenuItem value="option3">Option 3</MenuItem>
                </Select>
              </FormControl>
            );
        }

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!currentValue}
                onChange={(e) => handleValueChange(currentFilter, e.target.checked)}
              />
            }
            label={`No ${displayLabel.toLowerCase()}`}
          />
        );

      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            value={currentValue}
            onChange={(e) => handleValueChange(currentFilter, e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        );

      case 'multiSelect':
        const getMultiSelectOptions = () => {
          switch (currentFilter.model) {
            case 'vehicle':
              return vehicles.slice().sort((a, b) => (a.customUniqueId || '').localeCompare(b.customUniqueId || ''));
            case 'location':
              return locations.slice().sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));
            case 'country':
              return countries.slice().sort((a, b) => a.name.localeCompare(b.name));
            case 'project':
              return projects.slice().sort((a, b) => (a.summary || '').localeCompare(b.summary || ''));
            case 'contactSupplier':
              return sortedSuppliers;
            case 'manufacturer':
              return manufacturers.slice().sort((a, b) => a.name.localeCompare(b.name));
            case 'category':
              // Under their category group headings when the API provides them, otherwise a flat list.
              return categoriesHaveGroups ? sortCategoriesByGroup(categories) : sortCategoriesByDisplayName(categories);
            case 'categoryGroup':
              return getCategoryGroups(categories);
            case 'subsidiary':
              return subsidiaries.slice().sort((a, b) => a.name.localeCompare(b.name));
            case 'dataType':
              return dataTypes.slice().sort((a, b) => a.dataType.localeCompare(b.dataType));
            case 'group':
              return sortGroupsByGhgCategory(groups);
            case 'measurementUnit':
              return measurementUnits.slice().sort((a, b) => a.name.localeCompare(b.name));
            case 'scope':
              return [
                { id: '1', name: 'Scope 1' },
                { id: '2', name: 'Scope 2' },
                { id: '3', name: 'Scope 3' }
              ];
            case 'emissionType':
              return emissionTypes.slice();
            case 'source':
              return EMISSION_SOURCE_OPTIONS.slice();
            case 'flagCategory':
              return FLAG_CATEGORY_OPTIONS.map(o => ({ id: o.value, name: o.label }));
            default:
              return [];
          }
        };

        const getMultiSelectLabel = (option: any) => {
          if (!option) return '';
          switch (currentFilter.model) {
            case 'vehicle':
              return option.customUniqueId || '';
            case 'location':
              return option.friendlyName || '';
            case 'country':
              return option.name || '';
            case 'project':
              return option.summary || '';
            case 'contactSupplier':
              return option.name || '';
            case 'manufacturer':
              return option.name || '';
            case 'category':
              return getCategoryDisplayName(option);
            case 'categoryGroup':
              return option.name || '';
            case 'subsidiary':
              return option.name || '';
            case 'dataType':
              return option.dataType || '';
            case 'group':
              return option ? formatGroupLabel(option as IGroupWithCategory, { includeScope: false }) : '';
            case 'measurementUnit':
              return option.name || '';
            case 'scope':
              return option.name || '';
            case 'emissionType':
              return option.name || '';
            case 'source':
              return option.name || '';
            case 'flagCategory':
              return option.name || '';
            default:
              return '';
          }
        };

        const categoriesHaveGroups = categories.some(category => !!category.friendlyCategoryGroup);
        const multiOptions = getMultiSelectOptions();
        const selectedOptions = Array.isArray(currentValue)
          ? multiOptions.filter((opt: any) => currentValue.includes(opt.id))
          : [];

        return (
          <Autocomplete
            multiple
            disableCloseOnSelect
            ListboxComponent={currentFilter.model === 'contactSupplier' ? SupplierMultiSelectListbox : ScrollableListbox}
            options={multiOptions}
            groupBy={currentFilter.model === 'group'
              ? (option) => (option as IGroupWithCategory).scope ? `Scope ${(option as IGroupWithCategory).scope}` : 'Other'
              : currentFilter.model === 'category' && categoriesHaveGroups
                ? (option) => getCategoryGroupLabel(option as ICategory)
                : undefined}
            getOptionLabel={getMultiSelectLabel}
            value={selectedOptions}
            onChange={(_, newValue) => {
              const ids = (newValue as any[]).map(opt => opt.id);
              handleValueChange(currentFilter, ids);
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
              return (
                <Box
                  component="li"
                  key={key}
                  sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                  {...rest}
                >
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {currentFilter.model === 'country' && (option as any).imageUrl && (
                    <img
                      loading="lazy"
                      width="20"
                      src={(option as any).imageUrl}
                      alt="country flag"
                    />
                  )}
                  {getMultiSelectLabel(option)}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={displayLabel}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: buttonAlignment === 'right' ? 'flex-end' : 'flex-start' }}>
        {/* Filter Button - Always rendered unless chips-only mode */}
        {!showChipsOnly && (
          <Box sx={{
            display: 'flex',
            justifyContent: buttonAlignment === 'right' ? 'flex-end' : 'flex-start'
          }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleOpen}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                minWidth: '100px',
                width: '100px'
              }}
            >
              <Typography>Filter</Typography>
            </Button>
          </Box>
        )}

        {/* Filter Chips - In normal flow, pushes content down */}
        {!showButtonOnly && Object.keys(filterNames).length > 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            mt: 1,
            justifyContent: buttonAlignment === 'right' ? 'flex-end' : 'flex-start'
          }}>
            {renderFilterChips()}
          </Box>
        )}
      </Box>

      <DialogWrapper
        open={open}
        onClose={handleClose}
        title="Filter"
        cancelHandler={handleClose}
        submitHandler={handleSave}
        isSubmitting={false}
        maxWidth="lg"
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            height: 460,
            overflow: 'hidden',
          }}
        >
          {/* Left Panel - Filter Categories */}
          <Box
            sx={{
              flex: '0 0 250px',
              borderRight: '1px solid',
              borderColor: 'divider',
              p: 2,
              backgroundColor: '#fafafa',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', flexShrink: 0 }}>
              Filter options
            </Typography>

            <Box
              sx={{
                flex: 1,
                position: 'relative',
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: '8px',
                height: 'calc(100% - 8px)',
                pb: 6,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#a8a8a8',
                }
              }}
            >
              <Stack
                spacing={1}
              >
                {filters
                  .slice()
                  .sort((a, b) => getFilterLabel(a).localeCompare(getFilterLabel(b)))
                  .map((filter) => {
                    const hasData = hasDataForFilter(filter);
                    const isDisabled = !hasData;
                    const isSelected = selectedFilters.includes(filter.name);

                    return isDisabled ? (
                      <Tooltip key={filter.name} title="No data" arrow>
                        <span>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                disabled
                                sx={{
                                  color: 'inherit',
                                  '&.Mui-checked': {
                                    color: 'inherit',
                                  },
                                }}
                              />
                            }
                            label={getFilterLabel(filter)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              mx: 0,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: isSelected ? 'primary.main' : 'transparent',
                              color: isSelected ? 'white' : 'text.disabled',
                              '&:hover': {
                                backgroundColor: isSelected ? 'primary.dark' : 'transparent',
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <FormControlLabel
                        key={filter.name}
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleCheckboxChange(filter.name, e)}
                            sx={{
                              color: 'inherit',
                              '&.Mui-checked': {
                                color: 'inherit',
                              },
                            }}
                          />
                        }
                        label={getFilterLabel(filter)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          mx: 0,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          width: '100%',
                          boxSizing: 'border-box',
                          backgroundColor: isSelected ? 'primary.main' : 'transparent',
                          color: isSelected ? 'white' : 'text.primary',
                          '&:hover': {
                            backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                          },
                        }}
                      />
                    );
                  })}
              </Stack>
            </Box>
          </Box>
          {/* Right Panel - Input Area */}
          <Box sx={{ flex: 1, p: 3, height: '100%', overflowY: 'auto' }}>
            {filters
              .slice()
              .sort((a, b) => getFilterLabel(a).localeCompare(getFilterLabel(b)))
              .filter(filter => selectedFilters.includes(filter.name))
              .map((filter, index) => (
                <React.Fragment key={filter.name}>
                  {index > 0 && <Divider sx={{ mb: 2 }} />}
                  <Box sx={{ mb: 2 }}>
                    {renderFilterInput(filter)}
                  </Box>
                </React.Fragment>
              ))}
          </Box>
        </Box>
      </DialogWrapper>
    </>
  );
};
